import React, { useState, useEffect } from 'react';
import { X, Plus, Layers, Sparkles, Loader2, Check, Trash2 } from 'lucide-react';

import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import Button from '../components/ui/Button';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';

import { useToast } from '../hooks/useToast';
import { client, fetchUtilAccounts } from '../apiHelper';

const DEPRECIATION_METHODS = [
    { label: "Straight Line", value: "STRAIGHT_LINE" },
    { label: "Declining Balance", value: "DECLINING_BALANCE" },
    { label: "Sum-of-the-Years' Digits", value: "SUM_OF_YEARS_DIGITS" },
];

export const AssetCategoryModal = ({ isOpen, onClose, organizationId, currentUser, onCategoryCreated }) => {
    const { showToast } = useToast();
    const [glAccounts, setGlAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [mode, setMode] = useState('single');

    // Single Category Form
    const [singleForm, setSingleForm] = useState({
        code: '',
        name: '',
        depreciation_method: 'STRAIGHT_LINE',
        default_useful_life_months: 60,
        default_salvage_value_pct: 0.00,
        asset_account_id: '',
        accum_depr_account_id: '',
        depr_expense_account_id: ''
    });

    // Multi-Line Form with Distinct GL Accounts per Row (Pre-populated from your COA)
    const [bulkCategories, setBulkCategories] = useState([
        { 
            code: 'PLANT-MACH', 
            name: 'Plant & Machinery', 
            default_useful_life_months: 120, 
            depreciation_method: 'STRAIGHT_LINE',
            asset_account_id: '1510',       // Plant & Machinery
            accum_depr_account_id: '1590',  // Accumulated Depreciation
            depr_expense_account_id: '5024' // Factory Depreciation
        },
        { 
            code: 'OFF-FURN', 
            name: 'Furniture & Equipment', 
            default_useful_life_months: 84, 
            depreciation_method: 'STRAIGHT_LINE',
            asset_account_id: '1520',       // Furniture & Equipment
            accum_depr_account_id: '1590',  // Accumulated Depreciation
            depr_expense_account_id: '6800' // Depreciation Expense - Office
        },
        { 
            code: 'VEH-FLEET', 
            name: 'Vehicles & Transport', 
            default_useful_life_months: 60, 
            depreciation_method: 'DECLINING_BALANCE',
            asset_account_id: '1530',       // Vehicles
            accum_depr_account_id: '1590',  // Accumulated Depreciation
            depr_expense_account_id: '6800' // Depreciation Expense - Office
        },
        { 
            code: 'BUILDING', 
            name: 'Building & Real Estate', 
            default_useful_life_months: 360, 
            depreciation_method: 'STRAIGHT_LINE',
            asset_account_id: '150200',     // Building @ Cost
            accum_depr_account_id: '1590',  // Accumulated Depreciation
            depr_expense_account_id: '6800' // Depreciation Expense - Office
        }
    ]);

    useEffect(() => {
        if (isOpen && currentUser) {
            setLoading(true);
            fetchUtilAccounts(currentUser)
                .then(accounts => {
                    setGlAccounts(accounts.map(a => ({
                        label: `${a.account_number || a.account_code ? (a.account_number || a.account_code) + ' - ' : ''}${a.name || a.account_name}`,
                        value: (a.gl_account_id || a.account_id).toString()
                    })));
                })
                .catch(() => showToast("Failed to load Chart of Accounts.", "error"))
                .finally(() => setLoading(false));
        }
    }, [isOpen, currentUser, showToast]);

    if (!isOpen) return null;

    const handleSingleSubmit = async (e) => {
        e.preventDefault();
        if (!organizationId) {
            showToast("Please select an organization first.", "error");
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                organization_id: parseInt(organizationId, 10),
                code: singleForm.code.trim().toUpperCase(),
                name: singleForm.name.trim(),
                depreciation_method: singleForm.depreciation_method,
                default_useful_life_months: parseInt(singleForm.default_useful_life_months, 10),
                default_salvage_value_pct: parseFloat(singleForm.default_salvage_value_pct || 0),
                asset_account_id: parseInt(singleForm.asset_account_id, 10),
                accum_depr_account_id: parseInt(singleForm.accum_depr_account_id, 10),
                depr_expense_account_id: parseInt(singleForm.depr_expense_account_id, 10),
            };

            const created = await client.postData("/fixed-assets/create-category", payload);
            showToast("Asset Category saved successfully!", "success");
            if (onCategoryCreated) onCategoryCreated();
            onClose();
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to create category.", "error");
        } finally {
            setSubmitting(false);
        }
    };

    const handleBulkSubmit = async (e) => {
        e.preventDefault();
        if (!organizationId) {
            showToast("Please select an organization first.", "error");
            return;
        }

        // Validate that every row has complete GL mappings
        const invalidRow = bulkCategories.find(cat => 
            !cat.code || !cat.name || !cat.asset_account_id || !cat.accum_depr_account_id || !cat.depr_expense_account_id
        );

        if (invalidRow) {
            showToast(`Row "${invalidRow.code || 'Un-named'}" is missing code, name, or GL account assignments.`, "error");
            return;
        }

        setSubmitting(true);
        try {
            const payloadList = bulkCategories.map(cat => ({
                organization_id: parseInt(organizationId, 10),
                code: cat.code.trim().toUpperCase(),
                name: cat.name.trim(),
                depreciation_method: cat.depreciation_method,
                default_useful_life_months: parseInt(cat.default_useful_life_months, 10),
                default_salvage_value_pct: 0.00,
                asset_account_id: parseInt(cat.asset_account_id, 10),
                accum_depr_account_id: parseInt(cat.accum_depr_account_id, 10),
                depr_expense_account_id: parseInt(cat.depr_expense_account_id, 10),
            }));

            // console.log({ categories: payloadList })

            const response = await client.postData("/accounting/bulk-create-asset-categories", { categories: payloadList });
            showToast(response.msg || `${payloadList.length} Categories seeded successfully!`, "success");
            if (onCategoryCreated) onCategoryCreated();
            onClose();
        } catch (err) {
            showToast(err.response?.data?.detail || "Bulk category seed failed.", "error");
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddBulkRow = () => {
        setBulkCategories(prev => [
            ...prev,
            { 
                code: '', 
                name: '', 
                default_useful_life_months: 60, 
                depreciation_method: 'STRAIGHT_LINE',
                asset_account_id: '',
                accum_depr_account_id: '1590',
                depr_expense_account_id: ''
            }
        ]);
    };

    const handleRemoveBulkRow = (index) => {
        setBulkCategories(prev => prev.filter((_, i) => i !== index));
    };

    const handleBulkRowChange = (index, field, value) => {
        setBulkCategories(prev => {
            const copy = [...prev];
            copy[index][field] = value;
            return copy;
        });
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-xl shadow-2xl border border-zinc-200 w-full max-w-5xl overflow-hidden my-8">
                {/* Header */}
                <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/50">
                    <div className="flex items-center gap-2">
                        <Layers className="text-indigo-600" size={20} />
                        <h3 className="text-base font-bold text-zinc-900">Add Asset Categories to Sub-Ledger</h3>
                    </div>
                    <button onClick={onClose} className="text-zinc-400 hover:text-zinc-600 rounded-lg p-1 transition-colors">
                        <X size={18} />
                    </button>
                </div>

                {/* Mode Selector */}
                <div className="px-6 pt-4 flex gap-3">
                    <button
                        type="button"
                        onClick={() => setMode('single')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                            mode === 'single'
                                ? 'bg-indigo-600 text-white'
                                : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                        }`}
                    >
                        Single Entry
                    </button>
                    <button
                        type="button"
                        onClick={() => setMode('bulk')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                            mode === 'bulk'
                                ? 'bg-indigo-600 text-white'
                                : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                        }`}
                    >
                        <Sparkles size={13} /> Multi-Line Bulk Setup (Distinct GL Accounts)
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {loading ? (
                        <div className="py-12 text-center text-zinc-500 text-xs">
                            <Loader2 className="animate-spin inline mr-2 text-indigo-600" size={18} /> Loading Chart of Accounts...
                        </div>
                    ) : mode === 'single' ? (
                        <form onSubmit={handleSingleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Input
                                    label="Category Code"
                                    placeholder="e.g., IT-EQP"
                                    value={singleForm.code}
                                    onChange={(e) => setSingleForm(p => ({ ...p, code: e.target.value }))}
                                    required
                                />
                                <Input
                                    label="Category Name"
                                    placeholder="e.g., IT Hardware & Laptops"
                                    value={singleForm.name}
                                    onChange={(e) => setSingleForm(p => ({ ...p, name: e.target.value }))}
                                    required
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-zinc-50 p-3.5 rounded-lg border border-zinc-200">
                                <Select
                                    label="Depreciation Method"
                                    value={singleForm.depreciation_method}
                                    onChange={(e) => setSingleForm(p => ({ ...p, depreciation_method: e.target.value }))}
                                    options={DEPRECIATION_METHODS}
                                    required
                                />
                                <Input
                                    label="Useful Life (Months)"
                                    type="number"
                                    value={singleForm.default_useful_life_months}
                                    onChange={(e) => setSingleForm(p => ({ ...p, default_useful_life_months: e.target.value }))}
                                    required
                                />
                                <Input
                                    label="Salvage Value (%)"
                                    type="number"
                                    step="0.01"
                                    value={singleForm.default_salvage_value_pct}
                                    onChange={(e) => setSingleForm(p => ({ ...p, default_salvage_value_pct: e.target.value }))}
                                />
                            </div>

                            <div className="space-y-3 bg-indigo-50/40 p-4 rounded-lg border border-indigo-100">
                                <h4 className="text-xs font-bold text-indigo-900 uppercase tracking-wider">GL Account Routing</h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    <SearchableSelectBox
                                        label="Gross Fixed Asset GL"
                                        options={glAccounts}
                                        value={singleForm.asset_account_id}
                                        onChange={(e) => setSingleForm(p => ({ ...p, asset_account_id: e.target.value }))}
                                        placeholder="Select Asset Account..."
                                    />
                                    <SearchableSelectBox
                                        label="Accum. Depr. GL"
                                        options={glAccounts}
                                        value={singleForm.accum_depr_account_id}
                                        onChange={(e) => setSingleForm(p => ({ ...p, accum_depr_account_id: e.target.value }))}
                                        placeholder="Select Accum Account..."
                                    />
                                    <SearchableSelectBox
                                        label="Depr. Expense GL"
                                        options={glAccounts}
                                        value={singleForm.depr_expense_account_id}
                                        onChange={(e) => setSingleForm(p => ({ ...p, depr_expense_account_id: e.target.value }))}
                                        placeholder="Select Expense Account..."
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
                                <Button type="button" onClick={onClose} variant="secondary" className="text-xs">Cancel</Button>
                                <Button type="submit" disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs">
                                    {submitting ? <Loader2 size={14} className="animate-spin mr-1" /> : <Check size={14} className="mr-1" />} Save Category
                                </Button>
                            </div>
                        </form>
                    ) : (
                        <form onSubmit={handleBulkSubmit} className="space-y-4">
                            <p className="text-xs text-zinc-500">
                                Each category line allows distinct GL account routing for Gross Assets and Depreciation Expenses matching your COA.
                            </p>

                            <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                                {bulkCategories.map((row, idx) => (
                                    <div key={idx} className="p-3 bg-zinc-50 border border-zinc-200 rounded-lg space-y-2">
                                        {/* Category Meta Header */}
                                        <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                                            <div className="md:col-span-2">
                                                <input
                                                    type="text"
                                                    placeholder="Code (e.g., VEH)"
                                                    value={row.code}
                                                    onChange={(e) => handleBulkRowChange(idx, 'code', e.target.value)}
                                                    className="w-full border border-zinc-300 rounded px-2 py-1 text-xs font-mono font-bold"
                                                    required
                                                />
                                            </div>
                                            <div className="md:col-span-4">
                                                <input
                                                    type="text"
                                                    placeholder="Category Name"
                                                    value={row.name}
                                                    onChange={(e) => handleBulkRowChange(idx, 'name', e.target.value)}
                                                    className="w-full border border-zinc-300 rounded px-2 py-1 text-xs font-medium"
                                                    required
                                                />
                                            </div>
                                            <div className="md:col-span-2">
                                                <input
                                                    type="number"
                                                    placeholder="Life (Months)"
                                                    value={row.default_useful_life_months}
                                                    onChange={(e) => handleBulkRowChange(idx, 'default_useful_life_months', e.target.value)}
                                                    className="w-full border border-zinc-300 rounded px-2 py-1 text-xs"
                                                    required
                                                />
                                            </div>
                                            <div className="md:col-span-3">
                                                <select
                                                    value={row.depreciation_method}
                                                    onChange={(e) => handleBulkRowChange(idx, 'depreciation_method', e.target.value)}
                                                    className="w-full border border-zinc-300 rounded px-1 py-1 text-xs bg-white"
                                                >
                                                    <option value="STRAIGHT_LINE">Straight Line</option>
                                                    <option value="DECLINING_BALANCE">Declining Bal.</option>
                                                </select>
                                            </div>
                                            <div className="md:col-span-1 text-right">
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveBulkRow(idx)}
                                                    className="text-red-500 hover:text-red-700 p-1"
                                                    title="Remove row"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Distinct GL Account Assignments per Row */}
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 pt-1 border-t border-zinc-200/60">
                                            <SearchableSelectBox
                                                options={glAccounts}
                                                value={row.asset_account_id}
                                                onChange={(e) => handleBulkRowChange(idx, 'asset_account_id', e.target.value)}
                                                placeholder="Asset Account..."
                                            />
                                            <SearchableSelectBox
                                                options={glAccounts}
                                                value={row.accum_depr_account_id}
                                                onChange={(e) => handleBulkRowChange(idx, 'accum_depr_account_id', e.target.value)}
                                                placeholder="Accum Depr Account..."
                                            />
                                            <SearchableSelectBox
                                                options={glAccounts}
                                                value={row.depr_expense_account_id}
                                                onChange={(e) => handleBulkRowChange(idx, 'depr_expense_account_id', e.target.value)}
                                                placeholder="Depr Expense Account..."
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <button
                                type="button"
                                onClick={handleAddBulkRow}
                                className="text-xs text-indigo-600 font-semibold flex items-center gap-1 hover:underline pt-1"
                            >
                                <Plus size={13} /> Add Extra Line
                            </button>

                            <div className="flex justify-end gap-2 pt-2 border-t border-zinc-100">
                                <Button type="button" onClick={onClose} variant="secondary" className="text-xs">Cancel</Button>
                                <Button type="submit" disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs">
                                    {submitting ? <Loader2 size={14} className="animate-spin mr-1" /> : <Sparkles size={14} className="mr-1" />} Save Multi-Line Categories
                                </Button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};